import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './AboutUs.css';

const AboutUs = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us — Azamra</title>
        <meta name="description" content="Learn about Azamra's mission to pioneer the future of capital markets across Africa with innovative financial technology." />
        <meta property="og:title" content="About Us — Azamra" />
        <meta property="og:description" content="Learn about Azamra's mission to pioneer the future of capital markets across Africa with innovative financial technology." />
        <meta property="og:url" content="https://www.azamra.com/about-us" />
        <link rel="canonical" href="https://www.azamra.com/about-us" />
      </Helmet>
      <Header />

      {/* About Us Hero Section */}
      <section className="about-hero-section">
        <video
          src="/assets/images/video2.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="about-hero-video"
        />
        <Container className="about-hero-content">
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12} className="text-center">
              <h1 className="about-hero-title">
                Azamra is building Africa's financial future.
              </h1>
              <p className="about-hero-subtitle">
                Combining global finance expertise with deep innovation to empower
                institutions and transform capital markets across Africa.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* About Azamra Section */}
      <section className="about-info-section">
        <Container>
          <Row className="align-items-center about-info-row">

            {/* Left: Text */}
            <Col lg={6} md={12} className="about-info-left">
              <h2 className="about-info-title">About Azamra</h2>
              <p className="about-info-para">
                Azamra was founded by a seasoned Wall Street veteran: a former JP Morgan and
                Citibank banker, who joined forces with a visionary tech entrepreneur celebrated for
                building innovative solutions.
              </p>
              <p className="about-info-para">
                As an African American diaspora-led company headquartered in New York, Azamra
                stands at the intersection of global finance leadership and local impact. The wealth
                of experience brought by the founders and an unwavering commitment have
                shaped Azamra into a transformative force, dedicated to empowering communities
                and redefining Africa's financial future.
              </p>
            </Col>

            {/* Right: Office Image */}
            <Col lg={6} md={12} className="about-info-right">
              <div className="about-info-img-wrap">
                <img
                  src="/assets/images/about.webp"
                  alt="Azamra Office"
                  className="about-info-img"
                />
              </div>
            </Col>

          </Row>

          {/* Divider */}
          <div className="about-section-divider" />

          {/* Our Vision Section */}
          <Row className="align-items-start about-vision-row">

            {/* Left */}
            <Col lg={4} md={12} className="about-vision-left">
              <h2 className="about-vision-title">Our Vision</h2>
              <p className="about-vision-subtitle">The Future We're Building</p>
            </Col>

            {/* Right */}
            <Col lg={8} md={12} className="about-vision-right">
              <p className="about-vision-text">
                We envision Africa with transformative,{' '}
                <span className="about-vision-highlight">inclusive financial systems, and globally competitive capital markets. Our goal is to empower</span>{' '}
                institutions and individuals through innovative financial solutions to participate{' '}
                <span className="about-vision-highlight">in the global economy.</span>
              </p>
            </Col>

          </Row>
        </Container>
      </section>
      {/* Why Us Section */}
      <section
        className="about-why-section"
        style={{ backgroundImage: `url('/assets/images/hero-bg1.webp')` }}
      >
        <Container>
          <Row>
            <Col xs={12} className="text-center">
              <h2 className="about-why-title">Why Us And Why Now.</h2>
              <p className="about-why-subtitle">
                Defining moment to build inclusive, transparent, and future-ready financial
                markets across Africa.
              </p>
            </Col>
          </Row>

          <div className="about-why-border-wrap">
            <Row className="about-why-row">

              {/* Card 1 */}
              <Col lg={3} md={12} className="about-why-col">
                <div className="about-why-card about-why-card-white">
                  <div className="about-why-icon-wrap">
                    <img src="/assets/images/icon8.webp" alt="Unlocking New Access" className="about-why-icon-img" />
                  </div>
                  <h3 className="about-why-card-title">Unlocking New Access</h3>
                  <p className="about-why-card-desc">
                    Tokenization enables broader participation in opportunities by
                    lowering barriers and expanding financial inclusion.
                  </p>
                </div>
              </Col>

              {/* Card 2 */}
              <Col lg={6} md={12} className="about-why-col">
                <div className="about-why-card about-why-card-yellow">
                  <div className="about-why-icon-wrap">
                    <img src="/assets/images/icon9.webp" alt="Built for Africa's Financial Future" className="about-why-icon-img" />
                  </div>
                  <h3 className="about-why-card-title">Built for Africa's Financial Future</h3>
                  <p className="about-why-card-desc">
                    Azamra was created at a defining moment as global markets
                    shift toward digital assets ownership powered by blockchain-
                    technology. We are building the foundation for more and
                    future-ready capital systems across Africa.
                  </p>
                </div>
              </Col>

              {/* Card 3 */}
              <Col lg={3} md={12} className="about-why-col">
                <div className="about-why-card about-why-card-white">
                  <div className="about-why-icon-wrap">
                    <img src="/assets/images/icon10.webp" alt="Designed for the Next Era" className="about-why-icon-img" />
                  </div>
                  <h3 className="about-why-card-title">Designed for the Next Era</h3>
                  <p className="about-why-card-desc">
                    With deep expertise in finance and technology, Azamra is
                    positioned to shape the next generation of African markets.
                  </p>
                </div>
              </Col>

            </Row>
          </div>
        </Container>
      </section>

      {/* Our Values Section */}
      <section className="about-values-section">
        <Container>
          <Row>
            <Col lg={6} md={12}>
              <h2 className="about-values-title">Our Values</h2>
              <p className="about-values-subtitle">
                The principles that guide our mission to transform Africa's
                financial future through innovation and trust.
              </p>
            </Col>
          </Row>

          <Row className="about-values-row">

            {/* Card 1 */}
            <Col lg={3} md={6} sm={6} xs={12} className="about-values-col">
              <div className="about-values-card">
                <div className="about-values-icon-wrap">
                  <img src="/assets/images/value1.webp" alt="Innovation First" className="about-values-icon-img" />
                </div>
                <h3 className="about-values-card-title">Innovation First</h3>
                <p className="about-values-card-desc">
                  Harnessing blockchain and technology to simplify markets
                  and unlock opportunity.
                </p>
              </div>
            </Col>

            {/* Card 2 */}
            <Col lg={3} md={6} sm={6} xs={12} className="about-values-col">
              <div className="about-values-card">
                <div className="about-values-icon-wrap">
                  <img src="/assets/images/value2.webp" alt="Purpose-Driven Impact" className="about-values-icon-img" />
                </div>
                <h3 className="about-values-card-title">Purpose-Driven Impact</h3>
                <p className="about-values-card-desc">
                  Turning Africa's challenges into scalable financial solutions that
                  empower communities.
                </p>
              </div>
            </Col>

            {/* Card 3 */}
            <Col lg={3} md={6} sm={6} xs={12} className="about-values-col">
              <div className="about-values-card">
                <div className="about-values-icon-wrap">
                  <img src="/assets/images/value3.webp" alt="Built on Trust" className="about-values-icon-img" />
                </div>
                <h3 className="about-values-card-title">Built on Trust</h3>
                <p className="about-values-card-desc">
                  Transparency, compliance, and accountability are at the core of
                  everything we build.
                </p>
              </div>
            </Col>

            {/* Card 4 */}
            <Col lg={3} md={6} sm={6} xs={12} className="about-values-col">
              <div className="about-values-card">
                <div className="about-values-icon-wrap">
                  <img src="/assets/images/value4.webp" alt="Build Africa for Africa" className="about-values-icon-img" />
                </div>
                <h3 className="about-values-card-title">Build Africa for Africa</h3>
                <p className="about-values-card-desc">
                  A commitment to creating financial systems designed for
                  Africa's future.
                </p>
              </div>
            </Col>

          </Row>
        </Container>
      </section>
      {/* CTA Banner Section */}
      <section className="about-cta-section">
        <Container>
          <div
            className="about-cta-banner"
            style={{ backgroundImage: `url('/assets/images/${isMobile ? 'cta1.webp' : 'cta.webp'}')` }}
          >
            <h2 className="about-cta-title">
              <span className="about-cta-title-bold">Azamra provides the secure infrastructure to</span>
              <span className="about-cta-title-regular">launch, manage, and scale tokenized assets globally</span>
            </h2>
            <div className="about-cta-badges">
              {/* <a href="#app-store">
                <img src="/assets/images/app-store-badge.webp" alt="Download on the App Store" className="about-cta-badge" />
              </a>
              <a href="#google-play">
                <img src="/assets/images/google-play-badge.webp" alt="Get it on Google Play" className="about-cta-badge" />
              </a> */}
              <a href="/contact" className="cta-demo-btn">Book a Demo</a>
            </div>
          </div>
        </Container>
      </section>
      <Footer />
    </>
  );
};

export default AboutUs;
